import { BookOpen, Quote } from 'lucide-react';

interface BookPreviewProps {
  onOrderClick: () => void;
}

export function BookPreview({ onOrderClick }: BookPreviewProps) {
  return (
    <section className="py-16 sm:py-24 bg-black/50 px-4 sm:px-6">
      <div className="container mx-auto max-w-4xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent">
            Citește un fragment din carte
          </h2>
          <div className="flex items-center justify-center gap-2 text-gray-400">
            <Quote className="w-4 h-4 text-red-500" />
            <p className="text-sm sm:text-base">
              Un stil direct și autentic, fără menajamente
            </p>
            <Quote className="w-4 h-4 text-red-500" />
          </div>
        </div>

        <div className="relative">
          {/* Decorative elements */}
          <div className="absolute -top-4 -left-4 w-24 h-24 bg-red-500/10 rounded-full blur-2xl" />
          <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-red-500/10 rounded-full blur-2xl" />
          
          {/* Book preview */}
          <div className="relative bg-gradient-to-br from-gray-900/95 to-black/95 backdrop-blur-lg rounded-2xl border border-red-500/20 overflow-hidden transform transition-all duration-500 hover:border-red-500/40 hover:shadow-[0_0_50px_rgba(239,68,68,0.2)]">
            {/* Chapter header */}
            <div className="bg-gradient-to-r from-red-900/50 to-black/50 p-6 sm:p-8 border-b border-red-500/20">
              <div className="flex items-center gap-3 mb-4">
                <BookOpen className="w-6 h-6 text-red-500" />
                <h3 className="text-xl sm:text-2xl font-bold text-white">
                  Capitolul 4
                </h3>
              </div>
              <p className="text-lg sm:text-xl font-semibold text-gray-200">
                "Societatea pulii: Cum ne bagă lumea-n depresie și alte nebunii"
              </p>
            </div>

            {/* Chapter content */}
            <div className="p-6 sm:p-8 space-y-6 text-gray-300 leading-relaxed">
              <p>
                Hai să vorbim despre sistemul educațional, această mașinărie de tocat suflete tinere. De la grădiniță până la facultate, ești presat să te conformezi, să memorezi informații inutile și să te întrebi constant dacă ești suficient de bun.
              </p>

              <p>
                Școala nu te pregătește pentru viață, te pregătește să fii un sclav corporatist obedient. Te învață să stai cuminte, să urmezi regulile și să nu pui întrebări incomode. Creativitatea? O ucid sistematic, ca pe-un pui de găină la abator.
              </p>

              <p>
                Notele devin mai importante decât învățarea reală. Ajungi să-ți măsori valoarea în funcție de niște cifre arbitrare date de profesori care abia așteaptă să se pensioneze. Un 10 te face să te simți ca un zeu, un 4 te face să vrei să-ți sapi groapa.
              </p>

              <p>
                Și ce pula mea se întâmplă cu presiunea asta de a ști exact ce vrei să faci cu viața ta la 18 ani? Trebuie să alegi o carieră, să te decizi pentru următorii 40 de ani, când abia ai învățat să-ți speli singur șosetele. E o rețetă perfectă pentru criză existențială și anxietate.
              </p>

              <div className="relative my-8">
                <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-transparent" />
                <blockquote className="relative border-l-4 border-red-500 pl-4 py-2 text-lg font-medium italic">
                  "Sistemul ne învață să fim niște roboței ascultători, pregătiți să intrăm în câmpul muncii ca niște zombie corporatiști."
                </blockquote>
              </div>

              <p>
                Gândirea critică? Dezvoltarea emoțională? Astea-s considerate "materii opționale", dacă sunt luate în considerare deloc.
              </p>

              <p>
                Și să nu începem despre bullying și presiunea socială din școli. Trebuie să fii cool, să ai hainele potrivite, să fii popular - altfel ești un ciudat, un paria. Multe probleme de sănătate mentală își au rădăcinile fix în perioada asta, când ești cel mai vulnerabil.
              </p>

              <p>
                Facultatea? O altă glumă proastă. Termini cu datorii cât Casa Poporului și cu un job la McDonald's, pentru că aparent diploma ta nu valorează nici cât hârtia pe care e tipărită.
              </p>

              <p>
                Rezultatul? O generație întreagă de adulți care se simt inadecvați, anxioși și deprimați, întrebându-se constant dacă sunt suficient de buni. Sistemul educațional nu ne pregătește pentru viață, ci pentru o existență de conformism și îndoială de sine.
              </p>

              <p>
                Poate ar fi timpul să regândim tot sistemul ăsta de căcat. Să-i învățăm pe copii cum să gândească, nu ce să gândească. Să punem accent pe dezvoltarea personală, nu doar pe memorarea unor informații inutile. Dar hei, asta ar însemna să creștem o generație de oameni care gândesc pentru ei înșiși, și nu putem avea asta, nu-i așa?
              </p>
            </div>

            {/* Page number */}
            <div className="flex justify-center p-4 border-t border-red-500/20">
              <span className="text-gray-500 font-serif italic">36</span>
            </div>
          </div>
        </div>

        {/* Call to action */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 mb-6">
            Acest capitol este doar un exemplu din cartea care te va ajuta să-ți transformi depresia în superputere.
          </p>
          <button 
            onClick={onOrderClick}
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-red-600 to-red-700 text-white text-base sm:text-lg px-6 sm:px-8 py-2 sm:py-3 rounded-full font-bold transform transition-all duration-500 hover:scale-105 shadow-[0_0_30px_rgba(239,68,68,0.3)] hover:shadow-[0_0_50px_rgba(239,68,68,0.5)]"
          >
            <span>COMANDĂ ACUM</span> <BookOpen className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
}
